import { supabase } from '../lib/supabase';

class TalentService {
  // Get all talent profiles with video counts
  async getAllTalentProfiles(searchTerm = '', sortBy = 'created_at', sortOrder = 'desc') {
    try {
      let query = supabase
        ?.from('talent_profiles')
        ?.select(`
          id,
          first_name,
          last_name,
          display_name,
          email,
          phone,
          website,
          instagram,
          youtube,
          tiktok,
          headshot_url,
          created_at,
          updated_at,
          created_by,
          updated_by,
          user_id
        `);

      if (searchTerm?.trim()) {
        query = query?.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      // Apply sorting
      const ascending = sortOrder === 'asc';
      query = query?.order(sortBy || 'created_at', { ascending });

      const { data, error } = await query;

      if (error) {
        return { data: null, error };
      }

      // Get video counts for all talents
      const talentIds = data?.map(talent => talent?.id) || [];
      let videoCounts = {};
      let roleCounts = {};
      
      if (talentIds?.length > 0) {
        // Fetch video counts from the mux videos table (talent_id column)
        const { data: videoData, error: videoError } = await supabase
          ?.from('videos')
          ?.select('talent_id')
          ?.in('talent_id', talentIds);

        if (!videoError && videoData) {
          videoCounts = videoData?.reduce((acc, video) => {
            acc[video?.talent_id] = (acc?.[video?.talent_id] || 0) + 1;
            return acc;
          }, {});
        }

        // Fetch role counts using assigned_talent_id
        const { data: roleData, error: roleError } = await supabase
          ?.from('roles')
          ?.select('assigned_talent_id')
          ?.in('assigned_talent_id', talentIds);

        if (!roleError && roleData) {
          roleCounts = roleData?.reduce((acc, role) => {
            acc[role?.assigned_talent_id] = (acc?.[role?.assigned_talent_id] || 0) + 1;
            return acc;
          }, {});
        }
      }

      // Transform data to include video_count and role_count
      const transformedData = data?.map(talent => ({
        ...talent,
        video_count: videoCounts?.[talent?.id] || 0,
        role_count: roleCounts?.[talent?.id] || 0
      }));

      return { data: transformedData, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get talent profile by ID
  async getTalentById(id) {
    try {
      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get talent roles and musicals summary
  async getTalentRolesAndMusicals(talentId) {
    try {
      const { data, error } = await supabase
        ?.from('song_videos')
        ?.select(`
          id,
          character_song_analyses(
            role_id,
            roles(
              id,
              character_name,
              role_type,
              is_principal,
              voice_type,
              age_range,
              vocal_range,
              musical_id,
              musicals(
                id,
                title,
                composer,
                lyricist,
                year_premiered,
                poster_url,
                genre
              )
            )
          )
        `)
        ?.eq('talent_id', talentId);

      if (error) {
        return { data: null, error };
      }

      // Process and deduplicate roles
      const rolesMap = new Map();
      const musicalsMap = new Map();

      data?.forEach(video => {
        const analysis = video?.character_song_analyses;
        if (analysis?.roles) {
          const role = analysis?.roles;
          const musical = role?.musicals;
          
          // Add role to map (deduplicate by role_id)
          if (!rolesMap?.has(role?.id)) {
            rolesMap?.set(role?.id, {
              id: role?.id,
              character_name: role?.character_name,
              role_type: role?.role_type,
              is_principal: role?.is_principal,
              voice_type: role?.voice_type,
              age_range: role?.age_range,
              vocal_range: role?.vocal_range,
              musical: {
                id: musical?.id,
                title: musical?.title,
                composer: musical?.composer,
                lyricist: musical?.lyricist,
                year_premiered: musical?.year_premiered,
                poster_url: musical?.poster_url,
                genre: musical?.genre
              }
            });
          }
          
          // Add musical to map (deduplicate by musical_id)
          if (musical && !musicalsMap?.has(musical?.id)) {
            musicalsMap?.set(musical?.id, musical);
          }
        }
      });

      return {
        data: {
          roles: Array?.from(rolesMap?.values()) || [],
          musicals: Array?.from(musicalsMap?.values()) || [],
          videoCount: data?.length || 0
        },
        error: null
      };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Create new talent profile
  async createTalent(talentData) {
    try {
      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.insert([talentData])
        ?.select()
        ?.single();

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Update talent profile
  async updateTalent(id, talentData) {
    try {
      const updateData = {
        ...talentData,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.update(updateData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Delete talent profile
  async deleteTalentProfile(id) {
    try {
      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.delete()
        ?.eq('id', id);

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get talent profile by user ID
  async getTalentProfileByUserId(userId) {
    try {
      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single();

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get talent analytics
  async getTalentAnalytics() {
    try {
      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.select('id, created_at');

      if (error) {
        return { data: null, error };
      }

      const stats = {
        total: data?.length || 0,
        recentCount: 0,
        byMonth: {}
      };

      const oneMonthAgo = new Date();
      oneMonthAgo?.setMonth(oneMonthAgo?.getMonth() - 1);

      data?.forEach(profile => {
        // Count recent profiles
        if (new Date(profile?.created_at) > oneMonthAgo) {
          stats.recentCount++;
        }

        // Count by month
        const month = new Date(profile?.created_at)?.toISOString()?.slice(0, 7); // YYYY-MM
        stats.byMonth[month] = (stats?.byMonth?.[month] || 0) + 1;
      });

      return { data: stats, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Search talent profiles
  async searchTalentProfiles(searchTerm) {
    return this.getAllTalentProfiles(searchTerm);
  }

  // Get talent profiles with user associations
  async getTalentProfilesWithUsers() {
    try {
      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.select(`
          *,
          user_profiles!talent_profiles_user_id_fkey(
            id,
            full_name,
            username,
            email,
            role
          )
        `)
        ?.order('created_at', { ascending: false });

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Batch create talent profiles
  async batchCreateTalentProfiles(talentDataArray) {
    try {
      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.insert(talentDataArray)
        ?.select();

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get talent profiles count by criteria
  async getTalentProfilesCount(filters = {}) {
    try {
      let query = supabase?.from('talent_profiles')?.select('id', { count: 'exact' });

      // Apply filters if provided
      if (filters?.created_after) {
        query = query?.gte('created_at', filters?.created_after);
      }
      if (filters?.created_before) {
        query = query?.lte('created_at', filters?.created_before);
      }

      const { count, error } = await query;

      return { data: count, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get talent videos
  async getTalentVideos(talentId) {
    try {
      const { data, error } = await supabase
        ?.from('song_videos')
        ?.select('*')
        ?.eq('talent_id', talentId)
        ?.order('created_at', { ascending: false });

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get talent videos by role and musical
  async getTalentVideosByRole(talentId, roleId) {
    try {
      const { data, error } = await supabase
        ?.from('song_videos')
        ?.select(`
          id,
          level,
          subsection,
          video_path,
          thumbnail_path,
          duration_seconds,
          file_size_bytes,
          created_at,
          song_id,
          character_analysis_id,
          character_song_analyses!inner(
            id,
            role_id,
            song_id,
            roles!inner(
              id,
              character_name,
              musical_id,
              musicals(
                id,
                title
              )
            ),
            songs(
              id,
              title
            )
          )
        `)
        ?.eq('talent_id', talentId)
        ?.eq('character_song_analyses.role_id', roleId)
        ?.order('level')
        ?.order('subsection');

      if (error) {
        return { data: null, error };
      }

      // Process videos to include song and role information
      const processedVideos = data?.map(video => {
        const analysis = video?.character_song_analyses;
        return {
          ...video,
          songTitle: analysis?.songs?.title || 'Unknown Song',
          characterName: analysis?.roles?.character_name || 'Unknown Character',
          musicalTitle: analysis?.roles?.musicals?.title || 'Unknown Musical',
          displayName: `${video?.level}${video?.subsection ? ` - ${video?.subsection}` : ''}`
        };
      }) || [];

      return { data: processedVideos, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Follow a talent
  async followTalent(talentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'User not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('talent_follows')
        ?.insert([{ user_id: user?.id, talent_id: talentId }])
        ?.select()
        ?.single();

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Unfollow a talent
  async unfollowTalent(talentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'User not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('talent_follows')
        ?.delete()
        ?.eq('user_id', user?.id)
        ?.eq('talent_id', talentId);

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Check if current user follows a talent
  async isFollowingTalent(talentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: false, error: null };
      }

      const { data, error } = await supabase
        ?.from('talent_follows')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.eq('talent_id', talentId)
        ?.maybeSingle();

      return { data: !!data, error };
    } catch (err) {
      return { data: false, error: err };
    }
  }

  // Get follower count for a talent
  async getTalentFollowerCount(talentId) {
    try {
      const { count, error } = await supabase
        ?.from('talent_follows')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('talent_id', talentId);

      return { data: count || 0, error };
    } catch (err) {
      return { data: 0, error: err };
    }
  }

  // Get role count for a talent
  async getTalentRoleCount(talentId) {
    try {
      const { count, error } = await supabase
        ?.from('roles')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('assigned_talent_id', talentId);

      return { data: count || 0, error };
    } catch (err) {
      return { data: 0, error: err };
    }
  }
}

// Create service instance
const talentService = new TalentService();

// Named exports for backward compatibility - CRITICAL: These must match the method names exactly
export const getTalentById = (id) => talentService?.getTalentById(id);
export const createTalentProfile = (talentData) => talentService?.createTalent(talentData);
export const updateTalentProfile = (id, talentData) => talentService?.updateTalent(id, talentData);
export const getTalentVideos = (talentId) => talentService?.getTalentVideos(talentId);
export const deleteTalentProfile = (id) => talentService?.deleteTalentProfile(id);
export const getTalentProfiles = (searchTerm, sortBy, sortOrder) => talentService?.getAllTalentProfiles(searchTerm, sortBy, sortOrder);
export const getTalentRolesAndMusicals = (talentId) => talentService?.getTalentRolesAndMusicals(talentId);
export const getTalentVideosByRole = (talentId, roleId) => talentService?.getTalentVideosByRole(talentId, roleId);
export const createTalent = (talentData) => talentService?.createTalent(talentData);
export const updateTalent = (id, talentData) => talentService?.updateTalent(id, talentData);
export const getAllTalentProfiles = (searchTerm, sortBy, sortOrder) => talentService?.getAllTalentProfiles(searchTerm, sortBy, sortOrder);
export const followTalent = (talentId) => talentService?.followTalent(talentId);
export const unfollowTalent = (talentId) => talentService?.unfollowTalent(talentId);
export const isFollowingTalent = (talentId) => talentService?.isFollowingTalent(talentId);
export const getTalentFollowerCount = (talentId) => talentService?.getTalentFollowerCount(talentId);
export const getTalentRoleCount = (talentId) => talentService?.getTalentRoleCount(talentId);

// Default export
export default talentService;